import { useCallback } from 'react'
import { useRouter } from 'next/router'

import useAppDispatch from '../../../store/hooks/useAppDispatch'
import { signOut } from '../store/authThunks'
import useAppStore from '../../../store/hooks/useAppStore'
import { authCustomerIsLoggedInSelector } from '../store/authSelectors'
import { preOrderUserSelector } from '../../preOrder/store/preOrderSelectors'
import { resetPreOrderState } from '../../preOrder/store/preOrderSlice'
import storedPreOrderUserToken from '../../preOrder/utils/storedPreOrderUserToken'
import localStorageUtils from '../../app/utils/localStorageUtils'
import useRouteMatch from '../../app/hooks/useRouteMatch'
import { AppRoutes } from '../../app/constants'
import useUpdateCheckoutCompanyId from '../../checkout/hooks/useUpdateCheckoutCompanyId'
import { clientContextIsCompanyUserSelector } from '../../clientContext/store/clientContextSelectors'
import { setPendingSignOutCheckoutUpdate } from '../store/authSlice'
import { outletCompanyOverrideMenuSelector } from '../../outlet/store/outletSelector'

type UseAppSignOutReturn = () => Promise<boolean>

export default function useAppSignOut(): UseAppSignOutReturn {
  const store = useAppStore()

  const router = useRouter()

  const dispatch = useAppDispatch()

  const updateCheckoutCompanyIfNeeded = useUpdateCheckoutCompanyId()

  const isPreOrderRoute = useRouteMatch([
    AppRoutes.Checkout,
    AppRoutes.PreOrder,
  ])

  const handleSignOut = useCallback(async () => {
    const isLoggedIn = authCustomerIsLoggedInSelector(store.getState())
    const preOrderUser = preOrderUserSelector(store.getState())
    const isCompanyUser = clientContextIsCompanyUserSelector(store.getState())
    const hasCompanyOverrideMenu = !!outletCompanyOverrideMenuSelector(
      store.getState(),
    )

    try {
      if (isCompanyUser) {
        if (hasCompanyOverrideMenu) {
          // Only wait for checkout update if there's a company override menu
          // Set pending sign-out checkout update state
          dispatch(setPendingSignOutCheckoutUpdate(true))

          const success = await updateCheckoutCompanyIfNeeded(null)

          if (!success) {
            // If checkout update fails, don't proceed with sign-out/ show override menu error modal
            return false
          }
          dispatch(setPendingSignOutCheckoutUpdate(false))
        } else {
          // If company user but no override menu, just update checkout without waiting
          updateCheckoutCompanyIfNeeded(null)
        }
      }

      if (isLoggedIn) {
        await dispatch(signOut()).unwrap()

        // Ask who they are ordering for again on the next sign-in
        localStorageUtils.removeItem('companyProfileId')
      }

      if (preOrderUser) {
        // Reset pre-order state
        dispatch(resetPreOrderState())

        // Remove stored pre-order token
        storedPreOrderUserToken.remove()

        // Remove cart meta from local storage
        localStorageUtils.removeItem('cartMeta')

        // Redirect to home if user is on Pre Order page
        if (isPreOrderRoute) {
          router.replace(AppRoutes.Home)
        }
      }
      return true
    } catch (error) {
      dispatch(setPendingSignOutCheckoutUpdate(false))
      // Resolve with false if sign out fails
      return false
    }
  }, [dispatch, isPreOrderRoute, router, store, updateCheckoutCompanyIfNeeded])

  return handleSignOut
}
